import { useDarkMode } from '@/contexts/DarkModeContext';
import Alert from 'antd/es/alert';
import Typography from 'antd/es/typography';
import React, { useCallback, useState } from 'react';
import { styled } from 'styled-components';
import { JsonEditor } from 'ui-components';

import type { InputBlockConfig, PipelineBlock } from '@mai/types';

// Helper function to convert JSON schema to type description (same as in InputNode)
const getSchemaTypeDescription = (
  schema: unknown,
  maxLength: number = 50,
): string => {
  if (!schema || typeof schema !== 'object') return 'any';

  const schemaObj = schema as Record<string, unknown>;

  // Handle basic types
  if (schemaObj.type) {
    const type = schemaObj.type as string;

    switch (type) {
      case 'string':
        return 'string';
      case 'number':
      case 'integer':
        return 'number';
      case 'boolean':
        return 'boolean';
      case 'null':
        return 'null';
      case 'array':
        if (schemaObj.items) {
          const itemType = getSchemaTypeDescription(schemaObj.items, 20);
          return `list(${itemType})`;
        }
        return 'list(any)';
      case 'object':
        if (schemaObj.properties) {
          const props = schemaObj.properties as Record<string, unknown>;
          const keys = Object.keys(props);

          if (keys.length === 0) return 'object({})';

          const propDescriptions: string[] = [];
          let currentLength = 8; // Start with 'object()'

          for (const key of keys) {
            const propType = getSchemaTypeDescription(props[key], 15);
            const propDesc = `"${key}": ${propType}`;

            // Check if adding this property would exceed max length
            if (currentLength + propDesc.length + 2 > maxLength) {
              // +2 for ', '
              propDescriptions.push('...');
              break;
            }

            propDescriptions.push(propDesc);
            currentLength += propDesc.length + 2;
          }

          const content = propDescriptions.join(', ');
          return `object({${content}})`;
        }
        return 'object({})';
      default:
        return type;
    }
  }

  // Handle union types (oneOf, anyOf, allOf)
  if (schemaObj.oneOf && Array.isArray(schemaObj.oneOf)) {
    const types = (schemaObj.oneOf as unknown[]).map((s) =>
      getSchemaTypeDescription(s, 15),
    );
    return types.join(' | ');
  }

  // Handle properties-only objects (implicit object type)
  if (schemaObj.properties) {
    return getSchemaTypeDescription(
      { ...schemaObj, type: 'object' },
      maxLength,
    );
  }

  return 'any';
};

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const EditorLabel = styled(Typography.Text)`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.marginXXS}px;
`;

const EditorHelp = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

const TypePreview = styled.div`
  padding: ${({ theme }) => theme.paddingXS}px;
  background: ${({ theme }) => theme.colorBgContainer};
  border: 1px solid ${({ theme }) => theme.colorBorder};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  font-family: ${({ theme }) => theme.fontFamilyCode};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

interface InputBlockEditorProps {
  block: PipelineBlock & { type: 'input' };
  onConfigChange: (config: InputBlockConfig) => void;
}

export const InputBlockEditor: React.FC<InputBlockEditorProps> = ({
  block,
  onConfigChange,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isDarkMode] = useDarkMode();

  // Convert the current JSON schema to a string for the editor with prettification
  const currentSchemaString = React.useMemo(() => {
    const schema = block.config.json_schema;

    // If no schema exists, return empty object
    if (!schema) {
      return '{}';
    }

    try {
      let jsonToStringify;

      // If it's already a string, try to parse it first
      if (typeof schema === 'string') {
        if ((schema as string).trim() === '') {
          return '{}';
        }
        jsonToStringify = JSON.parse(schema);
      } else {
        // It's already an object
        jsonToStringify = schema;
      }

      // If it's an empty object, return formatted empty object
      if (
        typeof jsonToStringify === 'object' &&
        Object.keys(jsonToStringify).length === 0
      ) {
        return '{}';
      }

      // Prettify the JSON
      return JSON.stringify(jsonToStringify, null, 2);
    } catch {
      // If parsing fails, return the original value if it's a string, otherwise empty object
      if (typeof schema === 'string') {
        return schema;
      }
      return '{}';
    }
  }, [block.config.json_schema]);

  // Get type description for preview
  const typeDescription = React.useMemo(() => {
    return getSchemaTypeDescription(block.config.json_schema);
  }, [block.config.json_schema]);

  const handleJsonChange = useCallback(
    (value: string) => {
      // Don't update config while user is typing - only when valid
      if (isValid) {
        try {
          const parsedSchema = JSON.parse(value);
          onConfigChange({
            ...block.config,
            json_schema: parsedSchema,
          });
        } catch {
          // Invalid JSON - don't update config
        }
      }
    },
    [block.config, onConfigChange, isValid],
  );

  const handleValidation = useCallback(
    (valid: boolean, errorMessage?: string) => {
      setIsValid(valid);
      setError(errorMessage || null);
    },
    [],
  );

  return (
    <EditorContainer>
      <EditorLabel>JSON Schema *</EditorLabel>
      <EditorHelp>
        JSON schema defining the structure and validation rules for this input
      </EditorHelp>

      {error && (
        <Alert
          type="error"
          message="Invalid JSON"
          description={error}
          showIcon
        />
      )}

      <JsonEditor
        value={currentSchemaString}
        onChange={handleJsonChange}
        onValidate={handleValidation}
        height="200px"
        placeholder='{"type": "string", "description": "Input description"}'
        theme={isDarkMode ? 'dark' : 'light'}
      />

      <TypePreview>
        <EditorLabel style={{ fontSize: '11px' }}>Inferred Type:</EditorLabel>{' '}
        {typeDescription}
      </TypePreview>
    </EditorContainer>
  );
};
